import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import { Conversation } from '../types';

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (conversationId: string) => void | Promise<void>;
}

const ConversationList = ({
  conversations,
  selectedConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
}: ConversationListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuConversationId, setOpenMenuConversationId] = useState<string | null>(null);
  const lastTouchSelectAtRef = useRef<number>(0);
  const touchIntentRef = useRef<{
    conversationId: string | null;
    startX: number;
    startY: number;
    moved: boolean;
  }>({ conversationId: null, startX: 0, startY: 0, moved: false });

  const filteredConversations = conversations.filter((conv) =>
    conv.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedConversations = [...filteredConversations].sort((a, b) => {
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;

    const aTime = a.lastMessage?.timestamp?.getTime?.() ?? a.createdAt.getTime();
    const bTime = b.lastMessage?.timestamp?.getTime?.() ?? b.createdAt.getTime();
    return bTime - aTime;
  });

  const getTimeDisplay = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Chiar acum';
    if (diffInMinutes < 60) return `${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} h`;
    const days = Math.floor(diffInMinutes / 1440);
    return days === 1 ? '1 zi' : `${days} zile`;
  };

  const getOnlineParticipants = (conversation: Conversation) => {
    return conversation.participants.filter((p) => p.isOnline).length;
  };

  const getParentChildrenLabel = (conversation: Conversation) => {
    if (conversation.type !== 'private') return null;
    const other = conversation.participants[0];
    if (!other || other.role !== 'parent') return null;
    const children = other.children ?? [];
    if (children.length === 0) return null;
    return children
      .map((c) => `${c.name}${c.classroomName ? ` (${c.classroomName})` : ''}`)
      .join(', ');
  };

  useEffect(() => {
    if (!openMenuConversationId) return;
    const onDocClick = () => setOpenMenuConversationId(null);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openMenuConversationId]);

  const selectConversation = (conversationId: string) => {
    setOpenMenuConversationId(null);
    onSelectConversation(conversationId);
  };

  const toggleMenu = (conversationId: string) => {
    setOpenMenuConversationId((prev) => (prev === conversationId ? null : conversationId));
  };

  return (
    <div className="flex flex-col h-full bg-card border-r border-border">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Mesaje</h2>
          <button
            onClick={onNewConversation}
            className="p-2 rounded-md text-primary hover:bg-primary/10 transition-colors duration-150"
            aria-label="Pornește o conversație nouă"
          >
            <Icon name="Plus" size={20} />
          </button>
        </div>
        <Input
          type="search"
          placeholder="Caută conversații..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {sortedConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6">
            <Icon name="MessageSquare" size={48} className="text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground text-center">
              {searchQuery ? 'Nu s-au găsit conversații' : 'Nu există conversații încă'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedConversations.map((conversation) => (
              <button
                key={conversation.id}
                onPointerDown={(e) => {
                  if (e.pointerType !== 'touch') return;
                  lastTouchSelectAtRef.current = Date.now();
                  touchIntentRef.current = {
                    conversationId: conversation.id,
                    startX: e.clientX,
                    startY: e.clientY,
                    moved: false,
                  };
                }}
                onPointerMove={(e) => {
                  if (e.pointerType !== 'touch') return;
                  if (touchIntentRef.current.conversationId !== conversation.id) return;
                  const dx = Math.abs(e.clientX - touchIntentRef.current.startX);
                  const dy = Math.abs(e.clientY - touchIntentRef.current.startY);
                  if (dx > 8 || dy > 8) touchIntentRef.current.moved = true;
                }}
                onPointerUp={(e) => {
                  if (e.pointerType !== 'touch') return;
                  if (touchIntentRef.current.conversationId !== conversation.id) return;
                  const moved = Boolean(touchIntentRef.current.moved);
                  touchIntentRef.current = { conversationId: null, startX: 0, startY: 0, moved: false };
                  if (moved) return;
                  selectConversation(conversation.id);
                }}
                onPointerCancel={() => {
                  touchIntentRef.current = { conversationId: null, startX: 0, startY: 0, moved: false };
                }}
                onClick={() => {
                  // If we already handled the touch via pointer event, ignore the follow-up click.
                  if (Date.now() - lastTouchSelectAtRef.current < 800) return;
                  selectConversation(conversation.id);
                }}
                className={`w-full text-left p-4 transition-colors duration-150 hover:bg-muted ${
                  selectedConversationId === conversation.id ? 'bg-muted' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    {conversation.avatar ? (
                      <Image
                        src={conversation.avatar}
                        alt={`${conversation.name} avatar`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                        {conversation.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {conversation.type === 'private' &&
                      conversation.participants[0]?.isOnline && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-success rounded-full border-2 border-card" />
                      )}
                    {conversation.type === 'group' && getOnlineParticipants(conversation) > 0 && (
                      <div className="absolute bottom-0 right-0 flex items-center justify-center w-5 h-5 bg-success text-white text-xs font-medium rounded-full border-2 border-card">
                        {getOnlineParticipants(conversation)}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground truncate">
                          {conversation.name}
                        </h3>
                        {conversation.isPinned && (
                          <Icon name="Pin" size={14} className="text-primary flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {conversation.lastMessage && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {getTimeDisplay(conversation.lastMessage.timestamp)}
                          </span>
                        )}

                        <div className="relative">
                          <div
                            role="button"
                            tabIndex={0}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
                            aria-label="Opțiuni conversație"
                            aria-expanded={openMenuConversationId === conversation.id}
                            onPointerDown={(e) => {
                              if (e.pointerType !== 'touch') return;
                              lastTouchSelectAtRef.current = Date.now();
                              e.stopPropagation();
                              touchIntentRef.current = {
                                conversationId: `menu:${conversation.id}`,
                                startX: e.clientX,
                                startY: e.clientY,
                                moved: false,
                              };
                            }}
                            onPointerMove={(e) => {
                              if (e.pointerType !== 'touch') return;
                              if (touchIntentRef.current.conversationId !== `menu:${conversation.id}`) return;
                              const dx = Math.abs(e.clientX - touchIntentRef.current.startX);
                              const dy = Math.abs(e.clientY - touchIntentRef.current.startY);
                              if (dx > 8 || dy > 8) touchIntentRef.current.moved = true;
                            }}
                            onPointerUp={(e) => {
                              if (e.pointerType !== 'touch') return;
                              if (touchIntentRef.current.conversationId !== `menu:${conversation.id}`) return;
                              const moved = Boolean(touchIntentRef.current.moved);
                              touchIntentRef.current = { conversationId: null, startX: 0, startY: 0, moved: false };
                              if (moved) return;
                              e.preventDefault();
                              e.stopPropagation();
                              toggleMenu(conversation.id);
                            }}
                            onPointerCancel={() => {
                              if (touchIntentRef.current.conversationId === `menu:${conversation.id}`) {
                                touchIntentRef.current = { conversationId: null, startX: 0, startY: 0, moved: false };
                              }
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleMenu(conversation.id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter' && e.key !== ' ') return;
                              e.preventDefault();
                              e.stopPropagation();
                              toggleMenu(conversation.id);
                            }}
                          >
                            <Icon name="MoreVertical" size={18} />
                          </div>

                          {openMenuConversationId === conversation.id && (
                            <div
                              className="absolute right-0 mt-1 w-52 bg-card border border-border rounded-md shadow-lg z-50"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <div
                                role="button"
                                tabIndex={0}
                                className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-muted transition-colors duration-150"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenMenuConversationId(null);
                                  if (
                                    !window.confirm(
                                      'Ștergi conversația din listă? (Mesajele pot reapărea la un mesaj nou)'
                                    )
                                  ) {
                                    return;
                                  }
                                  await onDeleteConversation(conversation.id);
                                }}
                                onKeyDown={async (e) => {
                                  if (e.key !== 'Enter' && e.key !== ' ') return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenMenuConversationId(null);
                                  if (
                                    !window.confirm(
                                      'Ștergi conversația din listă? (Mesajele pot reapărea la un mesaj nou)'
                                    )
                                  ) {
                                    return;
                                  }
                                  await onDeleteConversation(conversation.id);
                                }}
                              >
                                Șterge conversația
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {conversation.type === 'group' && (
                      <p className="text-xs text-muted-foreground mb-1">
                        {conversation.participants.length} participanți
                      </p>
                    )}

                    {conversation.type === 'private' && getParentChildrenLabel(conversation) && (
                      <p className="text-xs text-muted-foreground mb-1 truncate">
                        Copil: {getParentChildrenLabel(conversation)}
                      </p>
                    )}

                    {conversation.lastMessage && (
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-muted-foreground truncate">
                          {conversation.lastMessage.senderName}:{' '}
                          {conversation.lastMessage.content}
                        </p>
                        {conversation.unreadCount > 0 && (
                          <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium text-white bg-primary rounded-full">
                            {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
